/**
 * PingPongDelayControls Component
 * 
 * Specialized control panel for the Ping-Pong Delay effect.
 * Displays 5 rotary knobs with tempo sync toggle and visual ping-pong indicator.
 */

import { RotaryKnob } from '../../ui/RotaryKnob';
import { PresetSelector } from './PresetSelector';
import type { EffectInstance, EffectDefinition } from '../../../services/effects/EffectsManager';
import { getParameterDescription } from '../../../constants/effectDescriptions';
import './PingPongDelayControls.css';

/**
 * Props for PingPongDelayControls component
 */
export interface PingPongDelayControlsProps {
  /** Effect instance */
  effect: EffectInstance;
  /** Effect definition */
  effectDefinition: EffectDefinition;
  /** Callback when parameter changes */
  onParameterChange: (parameterId: string, value: number) => void;
  /** Callback when multiple parameters change at once (for presets) */
  onParametersChange?: ((parameters: Record<string, number>) => void) | undefined;
  /** Whether controls are disabled */
  disabled?: boolean;
}

/**
 * PingPongDelayControls component
 * 
 * Layout:
 * - Top row: 3 knobs (time, feedback, stereoWidth)
 * - Bottom row: 2 knobs (filter, mix)
 * - Visual ping-pong indicator
 * 
 * @param props - Component props
 * @returns PingPongDelayControls component
 */
export function PingPongDelayControls({
  effect,
  effectDefinition,
  onParameterChange,
  onParametersChange,
  disabled = false,
}: PingPongDelayControlsProps) {
  /**
   * Get parameter definition by ID
   */
  const getParamDef = (id: string) => {
    return effectDefinition.parameters.find(p => p.id === id);
  };

  /**
   * Get parameter value
   */
  const getParamValue = (id: string) => {
    const paramDef = getParamDef(id);
    return effect.parameters[id] ?? paramDef?.default ?? 0;
  };

  /**
   * Handle preset selection
   */
  const handlePresetSelect = (presetParameters: Record<string, number>) => {
    // Use batch update if available, otherwise fall back to individual updates
    if (onParametersChange) {
      onParametersChange(presetParameters);
    } else {
      // Fallback: Apply parameters individually with delays
      Object.entries(presetParameters).forEach(([paramId, value], index) => {
        setTimeout(() => {
          onParameterChange(paramId, value);
        }, index * 10);
      });
    }
  };

  return (
    <div className="pingpong-delay-controls">
      {/* Preset Selector */}
      <PresetSelector
        effectType={effect.type}
        onPresetSelect={handlePresetSelect}
        disabled={disabled}
      />

      {/* Top row: Time, Feedback, Stereo Width */}
      <div className="pingpong-delay-controls-row">
        {['time', 'feedback', 'stereoWidth'].map((paramId) => {
          const paramDef = getParamDef(paramId);
          if (!paramDef) return null;
          
          const value = getParamValue(paramId);
          const tooltip = getParameterDescription(effect.type, paramId);
          
          return (
            <RotaryKnob
              key={paramId}
              value={value}
              min={paramDef.min}
              max={paramDef.max}
              default={paramDef.default}
              step={paramDef.step}
              label={paramDef.name}
              {...(paramDef.unit && { unit: paramDef.unit })}
              onChange={(val) => onParameterChange(paramId, val)}
              disabled={disabled}
              size="medium"
              {...(tooltip && { tooltip })}
            />
          );
        })}
      </div>

      {/* Bottom row: Filter, Mix */}
      <div className="pingpong-delay-controls-row">
        {['filter', 'mix'].map((paramId) => {
          const paramDef = getParamDef(paramId);
          if (!paramDef) return null;
          
          const value = getParamValue(paramId);
          const tooltip = getParameterDescription(effect.type, paramId);
          
          return (
            <RotaryKnob
              key={paramId}
              value={value}
              min={paramDef.min}
              max={paramDef.max}
              default={paramDef.default}
              step={paramDef.step}
              label={paramDef.name}
              {...(paramDef.unit && { unit: paramDef.unit })}
              onChange={(val) => onParameterChange(paramId, val)}
              disabled={disabled}
              size="medium"
              {...(tooltip && { tooltip })}
            />
          );
        })}
      </div>

      {/* Visual Ping-Pong Indicator */}
      <div className="pingpong-delay-indicator">
        <div className="pingpong-delay-indicator-label">Ping-Pong Pattern</div>
        <div className="pingpong-delay-indicator-visual">
          <div className="pingpong-delay-dot pingpong-delay-dot-left">L</div>
          <div className="pingpong-delay-arrow">→</div>
          <div className="pingpong-delay-dot pingpong-delay-dot-right">R</div>
          <div className="pingpong-delay-arrow">→</div>
          <div className="pingpong-delay-dot pingpong-delay-dot-left">L</div>
        </div>
      </div>
    </div>
  );
}
